/**
 * People — Figma `People · desktop (≥1024)` (3131:39219), `People states` (3133:92865).
 * Relation and age catalogues, the gender each age offers, draft ⇄ API.
 */

import type { Person, PersonPayload } from '@/types/people'

/** `07 Relation menu`: one choice from a fixed list. */
export const FAIR_PROFILE_V2_RELATIONS = [
  'partner',
  'mother',
  'father',
  'child',
  'sibling',
  'friend',
  'colleague',
  'other',
] as const

/** `08 Age menu`. Stored with a hyphen; shown with an en dash. */
export const FAIR_PROFILE_V2_AGE_RANGES = [
  '0-2',
  '3-6',
  '7-12',
  '13-17',
  '18-25',
  '26-35',
  '36-45',
  '46-55',
  '56+',
] as const

const CHILD_AGES = new Set(['0-2', '3-6', '7-12', '13-17'])

export type FairProfileV2PersonGender = 'woman' | 'man' | 'girl' | 'boy'

/** "Age range drives Woman/Man vs Girl/Boy." Before an age is picked: adults. */
export function fairProfileV2GendersForAge(age: string | null): FairProfileV2PersonGender[] {
  return age && CHILD_AGES.has(age) ? ['girl', 'boy'] : ['woman', 'man']
}

export function formatFairProfileV2AgeRange(age: string | null | undefined): string {
  return age ? age.replace('-', '–') : ''
}

/** The API has `kids` only; until it stores a child's sex the browser keeps it (BE-150). */
export const FAIR_PROFILE_V2_CHILD_SEX_KEY = 'fair.people.childSex'

export function readFairProfileV2ChildSex(): Record<string, 'girl' | 'boy'> {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(window.localStorage.getItem(FAIR_PROFILE_V2_CHILD_SEX_KEY) ?? '{}')
  } catch {
    return {}
  }
}

export function writeFairProfileV2ChildSex(id: number, sex: 'girl' | 'boy' | null): void {
  if (typeof window === 'undefined') return
  const map = readFairProfileV2ChildSex()
  if (sex) map[String(id)] = sex
  else delete map[String(id)]
  window.localStorage.setItem(FAIR_PROFILE_V2_CHILD_SEX_KEY, JSON.stringify(map))
}

/** What the card and the dialog show for a person's gender. */
export function resolveFairProfileV2PersonGender(
  person: Pick<Person, 'id' | 'gender'>,
  childSex: Record<string, 'girl' | 'boy'> = {},
): FairProfileV2PersonGender | null {
  const gender = person.gender?.toLowerCase()
  if (gender === 'woman' || gender === 'female') return 'woman'
  if (gender === 'man' || gender === 'male') return 'man'
  if (gender === 'kids') return childSex[String(person.id)] ?? null
  return null
}

export interface FairProfileV2PersonDraft {
  name: string
  relation: string | null
  age: string | null
  gender: FairProfileV2PersonGender | null
}

export function emptyFairProfileV2PersonDraft(): FairProfileV2PersonDraft {
  return { name: '', relation: null, age: null, gender: null }
}

export function toFairProfileV2PersonDraft(
  person: Person,
  childSex: Record<string, 'girl' | 'boy'> = {},
): FairProfileV2PersonDraft {
  return {
    name: person.profileName ?? '',
    relation: person.relationship,
    age: person.ageRange,
    gender: resolveFairProfileV2PersonGender(person, childSex),
  }
}

/** A gender that the new age no longer offers is dropped (Woman → a 7–12 child). */
export function withFairProfileV2Age(
  draft: FairProfileV2PersonDraft,
  age: string,
): FairProfileV2PersonDraft {
  const gender =
    draft.gender && fairProfileV2GendersForAge(age).includes(draft.gender) ? draft.gender : null
  return { ...draft, age, gender }
}

/** `05 filled`: name, relation and age are required; gender is optional. */
export function isFairProfileV2PersonComplete(draft: FairProfileV2PersonDraft): boolean {
  return Boolean(draft.name.trim() && draft.relation && draft.age)
}

export function isFairProfileV2PersonChanged(
  draft: FairProfileV2PersonDraft,
  saved: FairProfileV2PersonDraft,
): boolean {
  return (
    draft.name.trim() !== saved.name.trim() ||
    draft.relation !== saved.relation ||
    draft.age !== saved.age ||
    draft.gender !== saved.gender
  )
}

/** The API stores `woman | man`, a child as `kids`. */
export function toFairProfileV2PersonPayload(draft: FairProfileV2PersonDraft): PersonPayload {
  const gender =
    draft.gender === 'girl' || draft.gender === 'boy' ? 'kids' : draft.gender
  return {
    profile_name: draft.name.trim().slice(0, 100),
    relationship: draft.relation,
    gender,
    age_range: draft.age,
  }
}

/** The user's own profile row is not one of their people. */
export function fairProfileV2VisiblePeople(people: Person[]): Person[] {
  return people
    .filter((person) => !person.isPrimary)
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || a.id - b.id)
}

/** Card meta line (3138:95814): relation · gender · age; missing parts are left out. */
export function fairProfileV2PersonMetaParts(
  person: Pick<Person, 'id' | 'gender' | 'relationship' | 'ageRange'>,
  childSex: Record<string, 'girl' | 'boy'> = {},
): { relation: string | null; gender: FairProfileV2PersonGender | null; age: string } {
  return {
    relation: person.relationship || null,
    gender: resolveFairProfileV2PersonGender(person, childSex),
    age: formatFairProfileV2AgeRange(person.ageRange),
  }
}

export function joinFairProfileV2PersonMeta(parts: (string | null | undefined)[]): string {
  return parts.filter((part): part is string => Boolean(part?.trim())).join(' · ')
}
